import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import type { DirectSalePriceQuote } from '@designdeck/shared';
import {
  commitDirectSaleDesign,
  getPriceQuote,
  getAccessParams,
  getSessionRef,
} from '@/api/directSale';
import type { Product } from '@/api/products';
import { useDirectSaleStore } from '@/stores/directSaleStore';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { isPreviewFrame } from '@/lib/preview-frame';
import { describeApiError, getApiErrorCode } from '@/lib/api-errors';
import { submitCheckout } from './checkoutHandover';
import { buildCommitPositions, commitKeyFor } from './directSaleCommit';
import { sizesForColor } from './direct-sale-sizes';
import { useDirectSaleMethod } from './useDirectSaleMethod';

/** The editor has not delivered every decorated canvas yet. */
class CommitNotReadyError extends Error {}

/**
 * Quote errors a fresh quote cures: the quote expired, the customer
 * already bought it and buys again, or it is linked to a design the
 * customer changed since (the quote key carries no artwork). Everything
 * else is shown.
 */
const STALE_QUOTE_CODES: ReadonlySet<string> = new Set([
  'quote_not_found',
  'quote_already_redeemed',
  'quote_already_linked',
]);

/**
 * One purchase at a time, whoever asks. The hook is mounted once per
 * surface — the phone's bar and the desktop's purchase column exist side by
 * side, and a window crossing the breakpoint unmounts the one that started
 * the purchase while it is still running. A lock per instance would let the
 * other surface start a second one.
 */
const submission = {
  inFlight: false,
  listeners: new Set<() => void>(),
};
function setInFlight(inFlight: boolean) {
  submission.inFlight = inFlight;
  submission.listeners.forEach((notify) => notify());
}
/**
 * Tests only: a test aborted mid-purchase would otherwise leave the lock
 * set for every test after it. Outside Vitest (which runs with MODE
 * "test") it does nothing, so no stray call from the app can unlock a
 * purchase that is still running.
 */
export function resetDirectSaleSubmission() {
  if (import.meta.env.MODE !== 'test') return;
  setInFlight(false);
}
function subscribeToSubmission(notify: () => void) {
  submission.listeners.add(notify);
  return () => {
    submission.listeners.delete(notify);
  };
}

/**
 * How a click on "buy" ended. The desktop confirms a delivered handover in
 * place; the phone ignores the value and relies on the toasts.
 */
export type DirectSaleCheckoutOutcome =
  | 'delivered'
  | 'not-delivered'
  | 'failed'
  | 'skipped';

/**
 * The quote and the handover to the buying system, shared by the checkout
 * step and the phone's buy sheet. One implementation on purpose: every amount
 * comes from the server quote and the payload is built from the same gallery,
 * so the two surfaces cannot hand over different things.
 *
 * "Kaufen" first commits the design (it becomes a design of the shop, its
 * print data frozen and linked to the quote) and only then hands over, with
 * the quote and the design. A failed commit hands over nothing and leaves
 * a translated reason in `checkoutError`.
 */
export function useDirectSaleCheckout(
  product: Product | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const isSubmitting = useSyncExternalStore(
    subscribeToSubmission,
    () => submission.inFlight
  );
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const productId = useDirectSaleStore((s) => s.productId);
  const colorId = useDirectSaleStore((s) => s.colorId);
  const storedSizeId = useDirectSaleStore((s) => s.sizeId);
  const quantities = useDirectSaleStore((s) => s.quantities);
  const decoratedPositions = useDirectSaleStore((s) => s.decoratedPositions);
  const masterPositions = useCanvasStateManager((s) => s.masterPositions);

  /**
   * The sizes the colour offers, in catalogue order. The quote refuses any
   * other (quote_size_unknown), so a stale size from resumed work must
   * never reach it, even before the bridge has pruned the store.
   */
  const offeredSizeIds = useMemo(
    () => sizesForColor(product, colorId).map((size) => size.id),
    [product, colorId]
  );
  const sizeId =
    storedSizeId !== null && offeredSizeIds.includes(storedSizeId)
      ? storedSizeId
      : null;

  /** A stale pick must never reach the quote, which would refuse it. */
  const { effectiveId: decorationMethodId } = useDirectSaleMethod(product);

  /** Sizes with quantities, in the product's own size order. */
  const items = useMemo(
    () =>
      offeredSizeIds.flatMap((id) => {
        const quantity = quantities[id] ?? 0;
        return quantity > 0 ? [{ sizeId: id, quantity }] : [];
      }),
    [offeredSizeIds, quantities]
  );

  const quoteKey = [
    'direct-sale',
    'quote',
    productId,
    colorId,
    sizeId,
    items,
    decoratedPositions,
    decorationMethodId,
  ];
  const fetchQuote = () =>
    getPriceQuote({
      productId: productId as string,
      colorId,
      sizeId,
      // Nothing chosen yet prices one piece: the sheet shows it as the
      // item price while the customer picks sizes.
      items: items.length > 0 ? items : undefined,
      positions: decoratedPositions,
      // Only a real pick is sent: an API from before the choice rejects
      // the unknown field (forbidNonWhitelisted), null included.
      ...(decorationMethodId ? { decorationMethodId } : {}),
      language: i18n.language,
    });

  // A reason shown for one configuration says nothing about the next.
  useEffect(() => {
    setCheckoutError(null);
  }, [
    productId,
    colorId,
    sizeId,
    items,
    decoratedPositions,
    decorationMethodId,
  ]);

  const quote = useQuery({
    // Keyed on the items: another quantity is another quote.
    queryKey: quoteKey,
    queryFn: fetchQuote,
    enabled: enabled && productId !== null,
  });

  /** Previews the editor already rendered — no separate mockup fetch needed. */
  const gallery = useMemo(
    () =>
      decoratedPositions.map((decorated) => {
        const position = product?.positions?.find(
          (p) => p.id === decorated.positionId
        );
        return {
          positionId: decorated.positionId,
          slug: position ? position.slug : null,
          name: position ? position.name : '',
          preview: position ? masterPositions[position.slug]?.preview : null,
          widthMm: decorated.widthMm,
          heightMm: decorated.heightMm,
        };
      }),
    [decoratedPositions, product, masterPositions]
  );

  const describeCommitError = (error: unknown): string => {
    if (error instanceof CommitNotReadyError) {
      return t('directSale.checkout.commitNotReady');
    }
    // A coded error names its reason; no response at all is the network;
    // a throttle says to wait. Anything else is a failed save.
    if (
      getApiErrorCode(error) ||
      (axios.isAxiosError(error) &&
        (!error.response || error.response.status === 429))
    ) {
      return describeApiError(error);
    }
    return t('directSale.checkout.commitFailed');
  };

  const commitFor = async (current: DirectSalePriceQuote) => {
    const positions = buildCommitPositions(
      decoratedPositions,
      product?.positions ?? [],
      useCanvasStateManager.getState().masterPositions
    );
    if (!positions || !productId) throw new CommitNotReadyError();
    const parts = {
      sessionRef: getSessionRef(),
      productId,
      colorId,
      sizeId,
      items,
      positions,
      // Another method is another production: it must commit another
      // design, never reuse the one made with the previous method.
      ...(decorationMethodId ? { decorationMethodId } : {}),
    };
    return commitDirectSaleDesign({
      commitKey: await commitKeyFor(parts),
      quoteId: current.quoteId,
      productId,
      colorId,
      sizeId,
      positions,
    });
  };

  /** Commit, then hand over; a stale quote is replaced once. */
  const commitAndHandOver = async () => {
    let current = await queryClient.ensureQueryData({
      queryKey: quoteKey,
      queryFn: fetchQuote,
    });
    let committed;
    try {
      committed = await commitFor(current);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (!code || !STALE_QUOTE_CODES.has(code)) throw error;
      await queryClient.invalidateQueries({
        queryKey: ['direct-sale', 'quote'],
        refetchType: 'none',
      });
      current = await queryClient.fetchQuery({
        queryKey: quoteKey,
        queryFn: fetchQuote,
        staleTime: 0,
      });
      committed = await commitFor(current);
    }
    return handOver(current, committed.designId);
  };

  /** Whether the buying system took the order. */
  const handOver = (
    current: DirectSalePriceQuote,
    designId: string
  ): boolean => {
    if (!productId) return false;
    // Read the previews from the store, not from `gallery`: the desktop
    // saves the position synchronously right before the click, and the
    // closure of the last render still holds the preview from before that.
    const livePositions = useCanvasStateManager.getState().masterPositions;
    const delivered = submitCheckout({
      shop: getAccessParams()?.shop ?? null,
      sessionRef: getSessionRef(),
      quoteId: current.quoteId,
      designId,
      language: current.language,
      productId,
      colorId,
      sizeId: current.items[0]?.sizeId ?? sizeId,
      items: current.items.map((item) => ({
        sizeId: item.sizeId,
        sizeName: item.sizeName,
        quantity: item.quantity,
      })),
      quantity: current.quantity,
      decorationMethod: current.decorationMethod,
      positions: decoratedPositions.map((decorated) => {
        const position = product?.positions?.find(
          (p) => p.id === decorated.positionId
        );
        return {
          positionId: decorated.positionId,
          widthMm: decorated.widthMm,
          heightMm: decorated.heightMm,
          previewDataUrl:
            (position ? livePositions[position.slug]?.preview : null) ?? null,
        };
      }),
      price: current,
    });

    if (delivered) return true;
    // The seller's test link opens the configurator without a shop around
    // it: nobody could take the order, and that is not an error.
    if (isPreviewFrame()) toast(t('directSale.checkout.testMode'));
    else toast.error(t('directSale.checkout.handoverFailed'));
    return false;
  };

  const handleCheckout = async (): Promise<DirectSaleCheckoutOutcome> => {
    // Read from the lock itself, not from the rendered state: that lands a
    // render late, and a double click must not commit twice.
    if (submission.inFlight || !quote.data || !productId) return 'skipped';
    setInFlight(true);
    setCheckoutError(null);
    try {
      return (await commitAndHandOver()) ? 'delivered' : 'not-delivered';
    } catch (error) {
      setCheckoutError(describeCommitError(error));
      return 'failed';
    } finally {
      setInFlight(false);
    }
  };

  return {
    quote,
    gallery,
    items,
    handleCheckout,
    isSubmitting,
    checkoutError,
  };
}
